import {
  Button,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  TimeDurationPicker,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import React from 'react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { GroupMultiSelect } from '../../Groups/GroupMultiSelect';
import { MutatePhysicalAtonFormValues } from './atonForm';
import { PiMapPin, PiMinusThin, PiPlusThin } from 'react-icons/pi';
import { DrawGeometryType, latLonToShape } from '@ais-ui/react-openlayers';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { useLocalization } from '@ais-ui/locale-management';

interface MutateFormProps {
  form: UseFormReturn<MutatePhysicalAtonFormValues>;
  onStartDrawing: (geometryType: DrawGeometryType, shape?: GeoJson) => void;
}

interface AlarmFormFieldProps {
  form: UseFormReturn<MutatePhysicalAtonFormValues>;
}

const AlarmFormField = ({ form }: AlarmFormFieldProps) => {
  const { messages } = useLocalization();

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'alarms',
  });

  return (
    <FormField
      key="alarms"
      control={form.control}
      name="alarms"
      render={({ field }) => (
        <FormItem>
          <div className="flex justify-between">
            <div className="flex space-x-4 items-center">
              <FormLabel>
                {messages.atonPhysicalDialogAlarmUsersLabel}
              </FormLabel>
            </div>
            <InfoIconTooltip>
              {messages.atonPhysicalDialogAlarmUsersTooltip}
            </InfoIconTooltip>
          </div>

          <FormControl onChange={() => form.clearErrors()}>
            <>
              {fields.map((alarm, index) => (
                <div
                  key={`${alarm.name}${alarm.email}${alarm.phone}${alarm.url}${index}`}
                  className="flex space-x-2"
                >
                  <Button
                    type="button"
                    size="icon"
                    disabled={true}
                    variant="ghost"
                    className="h-9 w-9 flex-shrink-0"
                    onClick={() => remove(index)}
                  >
                    {index + 1}
                  </Button>
                  <Input
                    type="text"
                    placeholder={
                      messages.atonPhysicalDialogAlarmUsersNamePlaceholder
                    }
                    defaultValue={alarm.name}
                    onChange={(e) => {
                      form.setValue(`alarms.${index}.name`, e.target.value);
                      fields[index].name = e.target.value;
                    }}
                  />
                  <Input
                    type="text"
                    placeholder={
                      messages.atonPhysicalDialogAlarmUsersEmailPlaceholder
                    }
                    defaultValue={alarm.email}
                    onChange={(e) => {
                      form.setValue(`alarms.${index}.email`, e.target.value);
                      fields[index].email = e.target.value;
                    }}
                  />
                  <Input
                    type="number"
                    placeholder={
                      messages.atonPhysicalDialogAlarmUsersPhonePlaceholder
                    }
                    defaultValue={alarm.phone}
                    autoComplete="off"
                    onChange={(e) => {
                      const numberValue = e.target.value
                        ? parseFloat(e.target.value)
                        : undefined;
                      form.setValue(`alarms.${index}.phone`, numberValue);
                      fields[index].phone = numberValue;
                    }}
                  />
                  <Input
                    type="text"
                    placeholder={
                      messages.atonPhysicalDialogAlarmUsersUrlPlaceholder
                    }
                    defaultValue={alarm.url}
                    autoComplete="off"
                    onChange={(e) => {
                      form.setValue(`alarms.${index}.url`, e.target.value);
                      fields[index].url = e.target.value;
                    }}
                  />

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="h-9 w-9 flex-shrink-0 flex-grow-0"
                        onClick={() => remove(index)}
                      >
                        <PiMinusThin className="h-4 w-4"></PiMinusThin>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {messages.atonPhysicalDialogAlarmUsersRemoveTooltip}
                    </TooltipContent>
                  </Tooltip>
                </div>
              ))}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="h-9 w-9"
                    onClick={() =>
                      append({
                        name: '',
                        email: '',
                        phone: undefined,
                        url: '',
                      })
                    }
                  >
                    <PiPlusThin className="h-4 w-4"></PiPlusThin>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {messages.atonPhysicalDialogAlarmUsersAddTooltip}
                </TooltipContent>
              </Tooltip>
            </>
          </FormControl>
          {form.formState.errors.alarms && (
            <p className="text-sm font-medium text-destructive">
              {messages.atonPhysicalDialogAlarmUsersGenericFormError}
            </p>
          )}
        </FormItem>
      )}
    />
  );
};

export const MutatePhysicalForm = ({
  form,
  onStartDrawing,
}: MutateFormProps) => {
  const { messages } = useLocalization();
  let existingShape: GeoJson | undefined = undefined;
  if (form.getValues('longitude') && form.getValues('latitude')) {
    existingShape = latLonToShape(
      form.getValues('latitude'),
      form.getValues('longitude')
    );
  }

  return (
    <div className="space-y-8">
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.atonPhysicalDialogNameLabel} *</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Input
                id="physical-aton-name-field"
                type="text"
                placeholder={messages.atonPhysicalDialogNamePlaceholder}
                autoComplete="off"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}        
        name="mmsi"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.atonPhysicalDialogMmsiLabel} *</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Input
                id="physical-aton-mmsi-field"
                type="number"
                placeholder={messages.atonPhysicalDialogMmsiPlaceholder}
                autoComplete="off"
                {...field}
                onChange={(e) => {
                  if (!e.target.value) {
                    field.onChange('');
                  } else {
                    const numberValue = parseFloat(e.target.value);
                    field.onChange(numberValue);
                  }
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="flex space-x-4">
        <FormField
          control={form.control}
          name="latitude"
          render={({ field }) => (
            <FormItem>
              <div className="flex justify-between">
                <FormLabel>
                  {messages.atonPhysicalDialogLatitudeLabel} *
                </FormLabel>
              </div>
              <FormControl onChange={() => form.clearErrors()}>
                <Input
                  id="physical-aton-latitude-field"
                  type="number"
                  placeholder={messages.atonPhysicalDialogLatitudePlaceholder}
                  autoComplete="off"
                  {...field}
                  onChange={(e) => {
                    if (!e.target.value) {
                      field.onChange('');
                    } else {
                      const numberValue = parseFloat(e.target.value);
                      field.onChange(numberValue);
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="longitude"
          render={({ field }) => (
            <FormItem>
              <div className="flex justify-between">
                <FormLabel>
                  {messages.atonPhysicalDialogLongitudeLabel} *
                </FormLabel>
              </div>
              <div className="flex space-x-4">
                <div className="space-y-2">
                  <FormControl onChange={() => form.clearErrors()}>
                    <Input
                      id="physical-aton-longitude-field"
                      type="number"
                      placeholder={
                        messages.atonPhysicalDialogLongitudePlaceholder
                      }
                      autoComplete="off"
                      {...field}
                      onChange={(e) => {
                        if (!e.target.value) {
                          field.onChange('');
                        } else {
                          const numberValue = parseFloat(e.target.value);
                          field.onChange(numberValue);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </div>
                <Button
                  id="physical-aton-choose-location-button"
                  type="button"
                  size="icon"
                  variant="outline"
                  className="h-10 w-10 flex-shrink-0 flex-grow-0"
                  onClick={() => onStartDrawing('Point', existingShape)}
                >
                  <PiMapPin className="h-4 w-4"></PiMapPin>
                </Button>
              </div>
            </FormItem>
          )}
        />
      </div>
      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>
                {messages.atonPhysicalDialogDescriptionLabel}
              </FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Textarea id="physical-aton-description" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        key="warningTimeout"
        control={form.control}
        name="warningTimeout"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>
                {messages.atonPhysicalDialogWarningTimeoutLabel} *
              </FormLabel>
              <InfoIconTooltip>
                {messages.atonPhysicalDialogWarningTimeoutTooltip}
              </InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <TimeDurationPicker
                value={field.value}
                onChange={field.onChange}
                format="HH:MM:SS"
              ></TimeDurationPicker>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        key="failureTimeout"
        control={form.control}
        name="failureTimeout"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>
                {messages.atonPhysicalDialogFailureTimeoutLabel} *
              </FormLabel>
              <InfoIconTooltip>
                {messages.atonPhysicalDialogFailureTimeoutTooltip}
              </InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <TimeDurationPicker
                value={field.value}
                onChange={field.onChange}
                format="HH:MM:SS"
              ></TimeDurationPicker>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="offPositionAlarm"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>
                {messages.atonPhysicalDialogOffPositionAlarmLabel}
              </FormLabel>
              <InfoIconTooltip>
                {messages.atonPhysicalDialogOffPositionAlarmTooltip}
              </InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                defaultValue={field.value}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={
                      messages.atonPhysicalDialogOffPositionAlarmSelectPlaceholder
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <div className="flex items-center">
                      <span className="block w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                      <p>
                        {messages.atonPhysicalDialogOffPositionAlarmSelectNone}
                      </p>
                    </div>
                  </SelectItem>
                  <SelectItem value="warning">
                    <div className="flex items-center">
                      <span className="block w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                      <p>
                        {
                          messages.atonPhysicalDialogOffPositionAlarmSelectWarning
                        }
                      </p>
                    </div>
                  </SelectItem>
                  <SelectItem value="failure">
                    <div className="flex items-center">
                      <span className="block w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                      <p>
                        {
                          messages.atonPhysicalDialogOffPositionAlarmSelectFailure
                        }
                      </p>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="selectedGroups"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{messages.atonPhysicalDialogShareWithLabel}</FormLabel>
            <FormControl onChange={() => form.clearErrors()}>
              <GroupMultiSelect {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <AlarmFormField form={form}></AlarmFormField>
    </div>
  );
};
